import { useState } from 'react';
import { Link } from 'react-router';
import { failureOf, type Failure } from '@/api/errors';
import { usePauseVendor, useResumeVendor, useSetVendorAllowance, useSetVendorPlan, useVendors } from '@/api/hooks';
import type { VendorUnitState, WireVendor, WireVendorUnit } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Badge, Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { FactList, RecordHeader, TokenPanel, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { Explainer } from '@/ui/context';
import { useWho, type Who } from '@/ui/who';
import { explainVendor, explainVendorUnit } from './explain-ops';
import { localIsoDate } from '@/today/dates';

/**
 * Plans and usage (ADR-0095): what each vendor gives free, what has been
 * used of it, where it stops, and what paid use a superadmin allowed.
 *
 * Nothing paid is used unless a superadmin allows it here. A unit stops at
 * its line — the free allowance less a margin, or what was allowed beyond
 * it — and the gateway refuses the call before it goes out. Raising a line,
 * recording a plan and resuming a paused vendor spend a single-use
 * `billing.change` token; lowering spends none.
 */
export const UNIT_CHIP: Record<VendorUnitState, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  free: { label: 'FREE', tone: 'accent' },
  near_limit: { label: 'NEAR THE LINE', tone: 'warning' },
  stopped: { label: 'STOPPED', tone: 'danger' },
  paid: { label: 'PAID USE', tone: 'warning' },
  paused: { label: 'PAUSED', tone: 'danger' },
  limit: { label: 'A LIMIT, NOT A PRICE', tone: 'neutral' },
};

const count = (n: number) => (Number.isInteger(n) ? n.toLocaleString('en-GB') : n.toLocaleString('en-GB', { maximumFractionDigits: 2 }));
const usd = (n: number) => `$${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIOD_WORDS = { day: 'a day', month: 'a month', plan: 'for the whole plan' } as const;

/** The vendors that need a person's eye: near a line, stopped, or paused. */
export function vendorsNeedingAttention(vendors: readonly WireVendor[]): WireVendor[] {
  return vendors.filter((v) => v.pause !== null || v.units.some((u) => u.state === 'near_limit' || u.state === 'stopped'));
}

type Flow =
  | { kind: 'plan'; vendor: WireVendor }
  | { kind: 'line'; vendor: WireVendor; unit: WireVendorUnit }
  | { kind: 'pause' | 'resume'; vendor: WireVendor }
  | null;

export function Plans() {
  const me = useMeOrThrow();
  const q = useVendors(me.isSuperadmin);
  const who = useWho();
  const [flow, setFlow] = useState<Flow>(null);

  if (!me.isSuperadmin) {
    return (
      <div className="p-6">
        <p className="m-0 text-body">What each vendor costs, and what may be paid, is a superadmin’s to set. Ask one if a tool has stopped.</p>
      </div>
    );
  }
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const { vendors, gatewayConfigured } = q.data;

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Who can do what"
        title="Plans and usage"
        subtitle="What each vendor gives free, what has been used, and where it stops. Nothing paid is used unless a superadmin allows it here; the gateway refuses the call before it goes out."
      />
      {!gatewayConfigured && (
        <p className="m-0 border-l-2 border-warning pl-3 text-body" data-testid="no-gateway">
          This deployment cannot reach the gateway, so a line can be read here but not moved: the edge would not learn of it.
        </p>
      )}
      <p className="m-0 text-dense text-secondary">
        Keys are on <Link className="inline-flex min-h-tap min-w-tap items-center text-accent underline" to="/connections">Connections</Link>. Who may use a tool is on{' '}
        <Link className="inline-flex min-h-tap min-w-tap items-center text-accent underline" to="/assignments">Assignments</Link>. This page is what it may cost.
      </p>
      {vendors.map((v) => (
        <VendorCard key={v.vendor} vendor={v} who={who} canChange={gatewayConfigured} onFlow={setFlow} />
      ))}
      {flow?.kind === 'plan' && <RecordPlan vendor={flow.vendor} onClose={() => setFlow(null)} />}
      {flow?.kind === 'line' && <SetLine vendor={flow.vendor} unit={flow.unit} onClose={() => setFlow(null)} />}
      {flow?.kind === 'pause' && <PauseVendor vendor={flow.vendor} onClose={() => setFlow(null)} />}
      {flow?.kind === 'resume' && <ResumeVendor vendor={flow.vendor} onClose={() => setFlow(null)} />}
    </div>
  );
}

function VendorCard({ vendor: v, who, canChange, onFlow }: { vendor: WireVendor; who: Who; canChange: boolean; onFlow: (f: Flow) => void }) {
  return (
    <section className="flex flex-col gap-4 rounded-brand border border-strong p-5" aria-label={v.name} data-testid={`vendor-${v.vendor}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-panel">{v.name}</h2>
        {v.pause && <Badge tone="danger">PAUSED</Badge>}
        {v.plan.ended && <Badge tone="danger">PLAN ENDED</Badge>}
        <span className="ml-auto flex flex-wrap gap-2">
          <Button variant="quiet" disabled={!canChange} onClick={() => onFlow({ kind: 'plan', vendor: v })}>
            Record the plan…
          </Button>
          {v.pause ? (
            <Button icon="play" variant="secondary" disabled={!canChange} onClick={() => onFlow({ kind: 'resume', vendor: v })}>Resume…</Button>
          ) : (
            <Button icon="pause" variant="quiet" disabled={!canChange} onClick={() => onFlow({ kind: 'pause', vendor: v })}>Pause</Button>
          )}
        </span>
      </div>
      {v.pause && (
        <p role="alert" className="m-0 border-l-2 border-danger pl-3 text-body" data-testid="vendor-paused">
          Paused by {v.pause.by}, {when(v.pause.at)}: {v.pause.reason}. Nothing reaches {v.name} until a superadmin resumes it.
        </p>
      )}
      <Explainer context={explainVendor(v, who)} />
      <FactList
        facts={[
          ['Plan', v.plan.recorded ? v.plan.planName : `${v.plan.planName} — not recorded yet; the catalogue’s figures stand`],
          ['Runs', v.plan.startsOn ? `${v.plan.startsOn} to ${v.plan.endsOn ?? 'no end'}${v.plan.daysLeft !== null && !v.plan.ended ? ` (${v.plan.daysLeft} days left)` : ''}` : null],
          ['Policy', v.policy],
          ['Figures read', `from the vendor’s own pages, ${v.verifiedOn}`],
        ]}
      />
      <div className="flex flex-col gap-3">
        {v.units.map((u) => (
          <UnitRow key={u.unit} vendor={v} who={who} unit={u} canChange={canChange} onChange={() => onFlow({ kind: 'line', vendor: v, unit: u })} />
        ))}
      </div>
    </section>
  );
}

function UnitRow({ vendor, who, unit: u, canChange, onChange }: { vendor: WireVendor; who: Who; unit: WireVendorUnit; canChange: boolean; onChange: () => void }) {
  const chip = UNIT_CHIP[u.state];
  const share = u.deploymentBudget || u.stop <= 0 ? null : Math.min(100, Math.round((u.used / u.stop) * 100));
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3" data-testid={`unit-${u.vendor}-${u.unit}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-body font-semibold">{u.label}</span>
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <Button variant="quiet" disabled={!canChange} onClick={onChange}>
          Change the line…
        </Button>
      </div>
      {share !== null && (
        <div className="h-2 w-full bg-raised" role="meter" aria-label={`${u.label} used`} aria-valuemin={0} aria-valuemax={u.stop} aria-valuenow={u.used}>
          <div className={`h-2 ${u.state === 'stopped' ? 'bg-danger' : u.state === 'near_limit' || u.state === 'paid' ? 'bg-warning' : 'bg-accent'}`} style={{ width: `${share}%` }} />
        </div>
      )}
      <Explainer context={explainVendorUnit(vendor, u, who)} />
      <FactList
        facts={[
          ['Used', `${count(u.used)} ${u.label} ${u.period === 'plan' ? 'so far' : `this ${u.period}`}`],
          ['Free', u.deploymentBudget ? 'Nothing: paid from the first call' : `${count(u.included)} ${PERIOD_WORDS[u.period]}`],
          [
            'Stops at',
            u.limitOnly
              ? `${count(u.stop)} per box (${u.stopPct}% of the vendor’s limit of ${count(u.included)} ${PERIOD_WORDS[u.period]}); nothing can be bought past it`
              : u.deploymentBudget
                ? 'the deployment’s COST_BUDGETS, until a superadmin records an allowance here'
                : `${count(u.stop)}${u.paidAllowed > 0 ? `, including ${count(u.paidAllowed)} paid use a superadmin allowed` : ` (${u.stopPct}% of what is free; no paid use allowed)`}`,
          ],
          ['Warns at', u.deploymentBudget ? null : count(u.warn)],
          ['At this pace', u.periodEnd && !u.limitOnly ? `${count(u.forecast)} by ${when(u.periodEnd)}` : null],
          ['Would cost', u.projectedCostUsd === null ? (u.limitOnly ? null : 'a paid plan, not a per-unit price') : usd(u.projectedCostUsd)],
          ['Counted by', Object.entries(u.bySource).map(([source, n]) => `${source.replace('_', ' ')} ${count(n ?? 0)}`).join(' · ') || null],
          ['Note', u.note],
          ['Source', <a key="s" className="inline-flex min-h-tap min-w-tap items-center text-accent underline" href={u.source} target="_blank" rel="noreferrer">{new URL(u.source).host}</a>],
          ['Set', u.recorded && u.decidedBy ? `by ${u.decidedBy}, ${when(u.decidedAt)}` : null],
        ]}
      />
    </div>
  );
}

function useTokenIfAsked() {
  const [token, setToken] = useState('');
  const needs = (f: Failure | null) => f?.kind === 'refused' && f.code === 'supervision_required';
  return { token, setToken, needs };
}

/** Whether a proposed line lets more be spent than the one standing: the same rule the platform applies. */
export function raisesSpend(
  current: Pick<WireVendorUnit, 'included' | 'stopPct' | 'paidAllowed'>,
  next: { included: number; stopPct: number; paidAllowed: number },
): boolean {
  const line = (a: { included: number; stopPct: number; paidAllowed: number }) =>
    a.paidAllowed > 0 ? a.included + a.paidAllowed : Math.floor((a.included * a.stopPct) / 100);
  return line(next) > line(current) || next.paidAllowed > current.paidAllowed || next.included > current.included;
}

function SetLine({ vendor, unit, onClose }: { vendor: WireVendor; unit: WireVendorUnit; onClose: () => void }) {
  const change = useSetVendorAllowance();
  const toast = useToast();
  const t = useTokenIfAsked();
  const [included, setIncluded] = useState(String(unit.included));
  const [warnPct, setWarnPct] = useState(String(unit.warnPct));
  const [stopPct, setStopPct] = useState(String(unit.stopPct));
  const [paid, setPaid] = useState(String(unit.paidAllowed));
  // The price per unit past what is free, in dollars (F11): what a lookup our
  // index saved is worth. Blank keeps what is on record.
  const [price, setPrice] = useState(unit.pricePerUnitUsd === null ? '' : String(unit.pricePerUnitUsd));
  const typedPrice = price.trim() === '' ? undefined : Number(price.trim().replace(',', '.'));
  const next = {
    included: Number(included), warnPct: Number(warnPct), stopPct: Number(stopPct), paidAllowed: Number(paid),
    ...(typedPrice === undefined ? {} : { pricePerUnitUsd: typedPrice }),
  };
  const valid =
    [next.included, next.paidAllowed].every((n) => Number.isFinite(n) && n >= 0) &&
    (typedPrice === undefined || (Number.isFinite(typedPrice) && typedPrice >= 0)) &&
    Number.isInteger(next.warnPct) && Number.isInteger(next.stopPct) && next.warnPct >= 1 && next.stopPct <= 100 && next.warnPct <= next.stopPct;
  const raising = valid && raisesSpend(unit, next);
  const failure = change.isError ? failureOf(change.error) : null;
  const unitPrice = typedPrice ?? unit.pricePerUnitUsd;
  const cost = unitPrice !== null && Number.isFinite(unitPrice) && next.paidAllowed > 0 ? usd(next.paidAllowed * unitPrice) : null;
  return (
    <Dialog
      open
      danger={raising}
      onOpenChange={(o) => !o && onClose()}
      kicker={raising ? 'Critical · billing.change' : 'Lowering · no token'}
      title={`${vendor.name}: ${unit.label}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      note={!valid ? 'Warning at or below the stop, both whole percentages, and a price of zero or more' : raising && t.token === '' ? 'The token first' : undefined}
      primary={{
        label: raising ? 'Spend the token and save' : 'Save',
        disabled: !valid || (raising && t.token === '') || change.isPending,
        onClick: () =>
          change.mutate(
            { vendor: vendor.vendor, unit: unit.unit, ...next, ...(t.token ? { supervisionToken: t.token } : {}) },
            { onSuccess: () => { toast({ text: `${vendor.name}’s ${unit.label} line is saved, and the gateway holds it now.` }); onClose(); } },
          ),
      }}
    >
      <Field label="Free each period" hint={`What the plan includes, ${PERIOD_WORDS[unit.period]}. The catalogue says ${count(unit.included)}.`}>
        <TextInput inputMode="decimal" value={included} onChange={(e) => setIncluded(e.target.value)} aria-label="Free" />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Field label="Warn at (%)">
          <TextInput inputMode="numeric" value={warnPct} onChange={(e) => setWarnPct(e.target.value)} aria-label="Warn at" />
        </Field>
        <Field label="Stop at (%)" hint="Below 100 keeps a margin for what the vendor counts and the platform cannot see.">
          <TextInput inputMode="numeric" value={stopPct} onChange={(e) => setStopPct(e.target.value)} aria-label="Stop at" />
        </Field>
      </div>
      {/* A vendor's limit is not for sale: nothing can be allowed past it (ADR-0099). */}
      {!unit.limitOnly && (
        <Field
          label="Paid use allowed beyond what is free"
          hint={`In ${unit.label}. Zero allows none.${cost ? ` At most ${cost} ${PERIOD_WORDS[unit.period]} at the vendor’s price.` : ''}`}
        >
          <TextInput inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} aria-label="Paid use allowed" />
        </Field>
      )}
      <Field label="Price per unit, $" hint={`What one of ${unit.label} costs past what is free, from the vendor’s price list. Blank until someone records it; what our index saved is said in euros from this.`}>
        <TextInput inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} aria-label="Price per unit" />
      </Field>
      {(raising || t.needs(failure)) && (
        <TokenPanel operation="billing.change" target={`billing:${vendor.vendor}:${unit.unit}`} token={t.token} onToken={t.setToken} canMint />
      )}
      {failure && !t.needs(failure) && <Refusal failure={failure} compact />}
    </Dialog>
  );
}

function RecordPlan({ vendor, onClose }: { vendor: WireVendor; onClose: () => void }) {
  const record = useSetVendorPlan();
  const toast = useToast();
  const [name, setName] = useState(vendor.plan.planName);
  const [starts, setStarts] = useState(vendor.plan.startsOn ?? localIsoDate(new Date()));
  const [ends, setEnds] = useState(vendor.plan.endsOn ?? '');
  const [token, setToken] = useState('');
  const valid = name.trim() !== '' && /^\d{4}-\d{2}-\d{2}$/.test(starts) && (ends === '' || (/^\d{4}-\d{2}-\d{2}$/.test(ends) && ends >= starts));
  return (
    <Dialog
      open
      danger
      onOpenChange={(o) => !o && onClose()}
      kicker="Critical · billing.change"
      title={`${vendor.name}’s plan`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      note={!valid ? 'A name and a start; an end on or after it' : token === '' ? 'The token first' : undefined}
      primary={{
        label: 'Spend the token and record',
        disabled: !valid || token === '' || record.isPending,
        onClick: () =>
          record.mutate(
            { vendor: vendor.vendor, planName: name.trim(), startsOn: starts, ...(ends ? { endsOn: ends } : {}), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${vendor.name}’s plan is recorded. After its last day, nothing it gave free is free.` }); onClose(); } },
          ),
      }}
    >
      <Field label="Plan">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} aria-label="Plan" />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Field label="Starts">
          <TextInput type="date" value={starts} onChange={(e) => setStarts(e.target.value)} aria-label="Starts" />
        </Field>
        <Field label="Last day" hint="Empty: it does not end.">
          <TextInput type="date" value={ends} onChange={(e) => setEnds(e.target.value)} aria-label="Last day" />
        </Field>
      </div>
      <TokenPanel operation="billing.change" target={`billing:${vendor.vendor}`} token={token} onToken={setToken} canMint />
      {record.isError && <Refusal failure={failureOf(record.error)} compact />}
    </Dialog>
  );
}

function PauseVendor({ vendor, onClose }: { vendor: WireVendor; onClose: () => void }) {
  const pause = usePauseVendor();
  const toast = useToast();
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Stopping · no token"
      title={`Pause ${vendor.name}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Pause it',
        disabled: reason.trim() === '' || pause.isPending,
        onClick: () =>
          pause.mutate({ vendor: vendor.vendor, reason: reason.trim() }, { onSuccess: () => { toast({ text: `${vendor.name} is paused. Nothing reaches it.` }); onClose(); } }),
      }}
    >
      <p className="m-0 text-body">Every call to {vendor.name} is refused at the gateway from now on. Resuming it spends a token.</p>
      <Field label="Why">
        <TextInput value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Why" />
      </Field>
      {pause.isError && <Refusal failure={failureOf(pause.error)} compact />}
    </Dialog>
  );
}

function ResumeVendor({ vendor, onClose }: { vendor: WireVendor; onClose: () => void }) {
  const resume = useResumeVendor();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      danger
      onOpenChange={(o) => !o && onClose()}
      kicker="Critical · billing.change"
      title={`Resume ${vendor.name}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      note={token === '' ? 'The token first' : undefined}
      primary={{
        label: 'Spend the token and resume',
        disabled: token === '' || resume.isPending,
        onClick: () =>
          resume.mutate(
            { vendor: vendor.vendor, ...(reason.trim() ? { reason: reason.trim() } : {}), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${vendor.name} is resumed.` }); onClose(); } },
          ),
      }}
    >
      {vendor.pause && <p className="m-0 text-body">It was paused by {vendor.pause.by}: {vendor.pause.reason}.</p>}
      <Field label="What changed" hint="For the record: why it is safe to reach again.">
        <TextInput value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="What changed" />
      </Field>
      <TokenPanel operation="billing.change" target={`billing:${vendor.vendor}`} token={token} onToken={setToken} canMint />
      {resume.isError && <Refusal failure={failureOf(resume.error)} compact />}
    </Dialog>
  );
}

/** Across the app, for a superadmin: a vendor near its line, stopped or paused. */
export function VendorBanner() {
  const me = useMeOrThrow();
  const q = useVendors(me.isSuperadmin);
  if (!me.isSuperadmin || !q.data) return null;
  const attention = vendorsNeedingAttention(q.data.vendors);
  if (attention.length === 0) return null;
  const words = attention.map((v) => {
    if (v.pause) return `${v.name} is paused`;
    const stopped = v.units.find((u) => u.state === 'stopped');
    return stopped ? `${v.name}’s ${stopped.label} stopped` : `${v.name} is near its line`;
  });
  return (
    <div role="status" className="flex flex-wrap items-center gap-2 border-b border-warning bg-raised px-4 py-2 text-dense" data-testid="vendor-banner">
      <Kicker tone="accent">Costs</Kicker>
      <span>{words.join('; ')}.</span>
      <Link className="inline-flex min-h-tap items-center text-accent underline" to="/plans">
        Plans and usage
      </Link>
    </div>
  );
}
